/**
 * Take over and Hand back, written to the log of the session that was driving
 * the page (RFC 0303 3e).
 *
 * The two are not the same kind of thing. Take over is a record: the agent
 * learns it mid-turn from its next action being refused, and an idle agent has
 * nothing to say to it. Hand back is a go-ahead, so it queues a turn and the
 * agent carries on with its errand without the person typing.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import { enqueueNotification } from './enqueue-run'

/** The person took `url` back. Appended only; no turn is queued. */
export async function recordPageTakenOver(
  repo: Repo,
  sessionId: string,
  page: { url: string; at?: number },
): Promise<void> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  new BranchLog(handle as never).appendPageTakenOver(page)
}

/**
 * The person handed `url` back. Appended, and a turn queued so the agent
 * resumes. False for a captured session, which records it and queues nothing,
 * the same rule every notification keeps.
 */
export async function enqueuePageHandedBack(
  repo: Repo,
  sessionId: string,
  page: { url: string; at?: number },
  opts?: { projectId?: string },
): Promise<boolean> {
  return enqueueNotification(
    repo,
    sessionId,
    (log) => log.appendPageHandedBack(page),
    opts,
  )
}
